import { ApolloError, gql, makeVar, useReactiveVar } from "@apollo/client";
import { datadogLogs } from "@datadog/browser-logs";

import { isServer } from "@shared/utils.environment";

import { LOGIN, LOGOUT } from "data/users/gql";
// eslint-disable-next-line import/no-cycle
import { extract } from "data/users/remote-updates";
// eslint-disable-next-line import/no-cycle
import { toPromoCodes } from "@shared/graphql.conversions";
import {
  ADDRESS_FRAGMENT,
  BASKET_UPDATE_FRAGMENT,
  CHECKOUT_RESULT_FRAGMENT,
  CHECKOUT_STATE_FRAGMENT,
  CLIENT_ACCESS_FRAGMENT,
  DISCOUNT_UPDATE_FRAGMENT,
  LOGIN_RESPONSE_FRAGMENT,
  MEMBERSHIP_FRAGMENT,
  MEMBER_INFO_FRAGMENT,
  ORDER_DETAIL_FRAGMENT,
  ORDER_RECORD_FRAGMENT,
  REORDER_PRODUCTS_FRAGMENT,
  VARIANT_FRAGMENT,
  VARIANT_FRAGMENT_V2
} from "@shared/graphql.fragments";
import { userId } from "lib/apollo/variables";
// eslint-disable-next-line import/no-cycle
import { createRequestContext, getClient, toCacheId } from "lib/apolloClient";
import type { BasketType } from "lib/basket";
import { resetBasket, setBasket } from "lib/basket";
import {
  clearNotifications,
  setNotifications
} from "@shared/utils.notifications";
import { setTermsAndConditions } from "lib/termsAndConditions";
// eslint-disable-next-line import/no-cycle
import { clearUser, setCheckout, setUser, userVar } from "lib/user";
import type {
  AddressInput,
  AddressType,
  BasicOrderType,
  BPFingerprintType,
  CheckoutUpdateResultType,
  ClientPlatform,
  DiscountUpdateResultType,
  IncludedDiscountType,
  LoginUpdateResultType,
  MemberInfoType,
  MemberPaymentInfoType,
  MembershipInvoiceSummaryType,
  MembershipModificationsInput,
  MembershipType,
  OrderRecordType,
  ProductSuggestionsType,
  RefreshTokenResultType,
  RejectedDiscountType,
  UpsMeType,
  VariantType,
  VariantTypeV2
} from "@shared/graphql.types";
// eslint-disable-next-line import/no-cycle
import { deriveUserSegment } from "utils/deriveUserSegment";
// eslint-disable-next-line import/no-cycle
import { gtmLogoutEvent, trackOrderBasketType } from "utils/gtm";
import { removeNull } from "utils/removeNull";
import { setUserSegment } from "@shared/utils.cookies";

// eslint-disable-next-line import/no-cycle
import { track } from "tracking/client";
import { clearBPFingerprint } from "../fingerprint";
import { getTrackingEventId } from "./utils/trackingEventId";
import { pushEventIdToGtmContext } from "utils/gtm/utils";
import { setClientAccess } from "lib/clientAccess";

export const requestInProgress = makeVar<boolean>(false);

export const useRequestInProgress = (): boolean =>
  useReactiveVar(requestInProgress);

export const GET_ORDER = gql`
  query getOrder($orderNumber: String!) {
    getOrder(orderNumber: $orderNumber) {
      ...OrderDetailFragment
    }
  }
  ${ORDER_DETAIL_FRAGMENT}
`;

export const LIST_ORDERS = gql`
  query listOrders {
    listOrders {
      ...OrderRecordFragment
    }
  }
  ${ORDER_RECORD_FRAGMENT}
`;

export const decodeToken = (input = "") => {
  try {
    return JSON.parse(atob(input.split(".")[1]));
  } catch (err) {
    console.error(err);
  }
  return {};
};

const setRefreshAfter = (time: number) => {
  if (isServer()) return;

  window.localStorage.setItem("refreshAfter", String(time));
};

export function ingestToken(v: string, ttl: number): void {
  const decodedToken = decodeToken(v);
  datadogLogs.setGlobalContext({
    customerNumber: decodedToken.customerNumber ?? "unknown_customer"
  });

  setRefreshAfter(Date.now() + ttl * 1000);
}

const ANONYMOUS_LOGIN = gql`
  mutation anonymousLogin(
    $basket: [ItemInput!]!
    $wishlist: [ItemInput!]!
    $promocodes: [String!]!
  ) {
    anonymousLogin(
      basket: $basket
      wishlist: $wishlist
      promocodes: $promocodes
    ) {
      ...LoginResponseFragment
    }
  }
  ${LOGIN_RESPONSE_FRAGMENT}
`;

const REFRESH_TOKEN = gql`
  mutation refreshToken {
    refreshToken {
      status
      token
      ttl
      clientAccess {
        ...ClientAccessFragment
      }
    }
  }
  ${CLIENT_ACCESS_FRAGMENT}
`;

const CHECKOUT = gql`
  mutation checkout(
    $basket: [ItemInput!]!
    $wishlist: [ItemInput!]!
    $promocodes: [String!]!
    $asGuest: Boolean!
    $paymentId: String
    $giftMembershipDetails: GiftMembershipDetailsInput
  ) {
    checkout(
      basket: $basket
      wishlist: $wishlist
      promocodes: $promocodes
      asGuest: $asGuest
      paymentId: $paymentId
      giftMembershipDetails: $giftMembershipDetails
    ) {
      ...CheckoutResultFragment
    }
  }
  ${CHECKOUT_RESULT_FRAGMENT}
`;

const FORGOT_PASSWORD = gql`
  mutation forgotPassword($email: String!) {
    forgotPassword(email: $email)
  }
`;

const RESET_PASSWORD = gql`
  mutation resetPassword($password: String!, $bpToken: String!) {
    resetPassword(password: $password, bpToken: $bpToken)
  }
`;

const GET_PROFILE_ADDRESS = gql`
  query getMyAddresses {
    getMyAddresses {
      ...AddressFragment
    }
  }
  ${ADDRESS_FRAGMENT}
`;
const SAVE_ADDRESS_PROFILE = gql`
  mutation saveMyAddressProfile($address: AddressInput!) {
    saveMyAddressProfile(address: $address) {
      ...AddressFragment
    }
  }
  ${ADDRESS_FRAGMENT}
`;

const UPDATE_ADDRESS_PROFILE = gql`
  mutation updateMyAddressProfile($address: AddressInput!) {
    updateMyAddressProfile(address: $address) {
      ...AddressFragment
    }
  }
  ${ADDRESS_FRAGMENT}
`;

const DELETE_ADDRESS_PROFILE = gql`
  mutation deleteMyAddressProfile($addressKey: String!) {
    deleteMyAddressProfile(addressKey: $addressKey) {
      ...AddressFragment
    }
  }
  ${ADDRESS_FRAGMENT}
`;

export const UPDATE_DISCOUNTS = gql`
  mutation updateDiscounts(
    $listId: ID!
    $version: Int!
    $promocodes: [String!]!
    $gwpSelection: String
  ) {
    updateDiscounts(
      listId: $listId
      version: $version
      promocodes: $promocodes
      gwpSelection: $gwpSelection
    ) {
      ...DiscountUpdateFragment
    }
  }
  ${DISCOUNT_UPDATE_FRAGMENT}
`;

export function ingestLoginResult(result: LoginUpdateResultType): void {
  if (!result) {
    return;
  }

  const { token, ttl, notifications, clientAccess, role } = result;

  if (result.user) {
    const user = setUser({
      ...result.user,
      isLoggedIn: role === "authenticated"
    });
    deriveUserSegment(user);
  }
  if (token && ttl) {
    ingestToken(token, ttl);
  } else {
    setRefreshAfter(-1);
  }

  setNotifications(notifications);

  setClientAccess(clientAccess);
}

async function ingestRefreshResult(token?: string) {
  if (!token) return;

  const user = userVar();
  const tokenDecoded = decodeToken(token);

  if (Object.keys(tokenDecoded).length) {
    const {
      email,
      userType,
      membershipSku,
      isMember,
      isFreeTrialAllowed,
      customerNumber
    } = tokenDecoded;

    // Partial updates on user
    const name = userType === "authenticated" ? user.name : "anonymous";

    const pricing = (() => {
      if (!user.pricing) return;

      return {
        ...user.pricing,
        priceTier:
          (isMember && membershipSku) || user.hasMembershipInBag
            ? "member"
            : "typical"
      } as const;
    })();

    const membership = (() => {
      if (!membershipSku) return;

      return {
        ...(user.membership as MembershipType),
        id: isMember && membershipSku ? membershipSku : "none:none"
      };
    })();

    const updatedUser = setUser({
      email,
      name,
      pricing,
      membership,
      isFreeTrialAllowed,
      isLoggedIn: userType === "authenticated",
      id: customerNumber
    });

    // User segment update
    // As opposed to the userSegment update in `ingestLoginResult` following an explicit & successful login action,
    // refresh update needs to check whether we're logged in or not to assign the lapsedmember value
    deriveUserSegment(updatedUser);
  }
}

export async function refreshToken(): Promise<string> {
  const result = await getClient().mutate<{
    refreshToken: RefreshTokenResultType;
  }>({ mutation: REFRESH_TOKEN });

  if (!result || !result.data) {
    throw new Error(); // @todo: pass error message
  }

  const { token, ttl, clientAccess } = result.data.refreshToken;

  setClientAccess(clientAccess);
  ingestToken(token, ttl);
  ingestRefreshResult(token);

  return token;
}

export async function anonymousLogin(): Promise<boolean> {
  try {
    const user = userVar();
    const { basket, wishlist, promocodes } = extract(user);

    const result = await getClient().mutate({
      mutation: ANONYMOUS_LOGIN,
      variables: {
        basket,
        wishlist,
        promocodes
      },
      update: (_cache, { data }) => {
        ingestLoginResult(data?.anonymousLogin);
      }
    });
    return !!result?.data?.anonymousLogin?.status;
  } catch (error) {
    setNotifications([error as Error]);
    return false;
  }
}

export const withGlobalSpinner = <TRes, TArgs extends unknown[]>(
  fn: (...args: TArgs) => Promise<TRes>
) => {
  return async (...args: TArgs): Promise<TRes> => {
    requestInProgress(true);

    try {
      return await fn(...args);
    } finally {
      requestInProgress(false);
    }
  };
};

export const addPromoCode = withGlobalSpinner(async function addPromoCode(
  code?: string,
  {
    onCodeApplied = () => undefined,
    onCodeRejected = () => undefined
  }: {
    onCodeApplied?: (d: IncludedDiscountType) => void;
    onCodeRejected?: (d: RejectedDiscountType) => void;
  } = {
    onCodeApplied: () => undefined,
    onCodeRejected: () => undefined
  },
  gwpSelection?: string
) {
  const user = userVar();

  const { promocodes = [] } = extract(user);

  if (code && promocodes.includes(code)) return Promise.reject();

  const result = await getClient().mutate<{
    updateDiscounts: DiscountUpdateResultType;
  }>({
    mutation: UPDATE_DISCOUNTS,
    variables: {
      listId: "",
      version: -1,
      promocodes: code && promocodes.concat(code),
      gwpSelection
    },
    context: createRequestContext({ sessionStrategy: "START_NEW_ANONYMOUS" })
  });

  if (result?.data?.updateDiscounts) {
    const {
      basket,
      discounts,
      pricing,
      includedDiscounts,
      rejectedDiscounts,
      notifications,
      gwpModule,
      thresholds,
      listId
    } = result.data.updateDiscounts;

    setUser({
      basket,
      basketId: listId,
      discounts,
      pricing,
      includedDiscounts,
      rejectedDiscounts
    });

    setBasket({ items: basket, gwpModule, thresholds });

    setNotifications(notifications);
  }

  const appliedDiscount = result.data?.updateDiscounts.includedDiscounts.find(
    (d) => d.code === code
  );
  const rejectedDiscount = result.data?.updateDiscounts.rejectedDiscounts.find(
    (d) => d.code === code
  );

  if (appliedDiscount) {
    onCodeApplied(appliedDiscount);
  }

  if (rejectedDiscount) {
    onCodeRejected(rejectedDiscount);
  }

  if (!result.data) throw new Error("No response received");

  return result.data.updateDiscounts;
});

export const REMOVE_DISCOUNTS = gql`
  mutation removeDiscount($promocode: String!) {
    removeDiscount(promocode: $promocode) {
      ...DiscountUpdateFragment
    }
  }
  ${DISCOUNT_UPDATE_FRAGMENT}
`;
export const removeCtPromoCode = withGlobalSpinner(
  async function removeCtPromoCode(promocode: string) {
    const result = await getClient().mutate<{
      removeDiscount: DiscountUpdateResultType;
    }>({
      mutation: REMOVE_DISCOUNTS,
      variables: {
        promocode
      },
      context: createRequestContext({ sessionStrategy: "START_NEW_ANONYMOUS" })
    });

    if (result?.data?.removeDiscount) {
      const {
        basket,
        discounts,
        pricing,
        includedDiscounts,
        rejectedDiscounts,
        notifications,
        thresholds,
        gwpModule,
        listId
      } = result.data.removeDiscount;

      setUser({
        basket,
        basketId: listId,
        discounts,
        pricing,
        includedDiscounts,
        rejectedDiscounts
      });

      setBasket({ gwpModule, thresholds, items: basket });

      setNotifications(notifications);
    }

    return !result?.errors;
  }
);

export async function login(
  email: string,
  password: string
): Promise<LoginUpdateResultType | undefined> {
  const user = userVar();
  const {
    basketId,
    basketVersion,
    basket,
    wishlistId,
    wishlistVersion,
    wishlist,
    promocodes,
    referralCode
  } = extract(user);

  const result = await getClient().mutate<{ login: LoginUpdateResultType }>({
    mutation: LOGIN,
    variables: {
      email,
      password,
      basketId,
      basketVersion,
      basket,
      wishlistId,
      wishlistVersion,
      wishlist,
      promocodes,
      referralCode
    },
    update: (_, { data }) => {
      if (data?.login) {
        ingestLoginResult(data.login);
      }
    }
  });

  if (!result.data?.login?.status) {
    return;
  }

  return result.data.login;
}

export async function logout(): Promise<boolean> {
  try {
    const result = await getClient().mutate({
      mutation: LOGOUT
    });

    return !!result?.data?.logout?.status;
  } catch (error) {
    setNotifications([error as Error]);
    throw error;
  } finally {
    const user = userVar();
    clearUser();
    clearNotifications();
    resetBasket();
    setUserSegment("nonmember");
    trackOrderBasketType("");
    clearBPFingerprint();
    const eventId = getTrackingEventId();

    if (user?.id && user.id !== "initial" && user?.name !== "anonymous") {
      pushEventIdToGtmContext(eventId);
      gtmLogoutEvent(user.id);
      track(
        {
          event: "Signed Out",
          properties: {
            member_id: user.id
          }
        },
        { event_id: eventId }
      );
    }
  }
}

const emptyCheckoutState: CheckoutUpdateResultType = {
  basketId: "",
  basketVersion: -1,
  items: [],
  notifications: [],
  discounts: [],
  wishes: [],
  wishlistId: "",
  wishlistVersion: -1,
  includedDiscounts: [],
  rejectedDiscounts: [],
  status: true
};

export const checkout = withGlobalSpinner(async function checkout(
  paymentId?: string,
  allowNoBasket?: boolean
) {
  const user = userVar();

  const result = await getClient()
    .mutate<{ checkout: CheckoutUpdateResultType }>({
      mutation: CHECKOUT,
      variables: {
        basket: user.basketItemInputs,
        wishlist: user.wishlistItemInputs,
        promocodes: toPromoCodes(user.discounts),
        paymentId,
        asGuest: !user.isMember && !user.hasMembershipInBag,
        giftMembershipDetails: user.giftMembershipDetails
      },
      context: createRequestContext({ sessionStrategy: "REDIRECT_TO_LOGIN" }),
      update: async (_cache, { data }) => {
        if (data?.checkout.checkoutState) {
          setCheckout({
            ...data?.checkout.checkoutState,
            nextPage: data?.checkout.nextPage
          });
          const {
            pricing,
            includedDiscounts,
            rejectedDiscounts,
            deliveryMethod
          } = data.checkout.checkoutState;

          setUser({
            pricing,
            includedDiscounts,
            rejectedDiscounts,
            deliveryMethod,
            basket: data.checkout.items
          });

          const basketUpdate: Partial<BasketType> = {
            items: data.checkout.items
          };

          if (data.checkout.gwpModule) {
            basketUpdate.gwpModule = data.checkout.gwpModule;
          }

          setBasket(basketUpdate);

          setTermsAndConditions(data?.checkout?.termsAndConditions);
        }
      }
    })
    .catch((err) => {
      if (
        allowNoBasket &&
        err instanceof ApolloError &&
        err.graphQLErrors.some(
          ({ extensions = {} }) => extensions.code === "CT_NO_ACTIVE_CART"
        )
      ) {
        // TODO replace with reactive var implementation similar to user
        getClient().writeFragment({
          id: toCacheId("CheckoutStateType", userId()),
          fragment: CHECKOUT_STATE_FRAGMENT,
          data: emptyCheckoutState,
          fragmentName: "CheckoutStateFragment"
        });

        return { data: { checkout: emptyCheckoutState } };
      }
    });

  if (!result?.data) throw new Error("No response received");

  return result.data.checkout;
});

export async function forgotPassword(email: string): Promise<void> {
  const result = await getClient().mutate({
    mutation: FORGOT_PASSWORD,
    variables: {
      email
    }
  });
  return result?.data;
}

export async function resetPassword(
  password: string,
  bpToken: string
): Promise<void> {
  const result = await getClient().mutate({
    mutation: RESET_PASSWORD,
    variables: {
      password,
      bpToken
    }
  });
  return result?.data;
}

export const getProfileAddresses = (options?: {
  runInBackground?: boolean;
}) => {
  const handler = async () => {
    const result = await getClient().query<{ getMyAddresses: AddressType[] }>({
      query: GET_PROFILE_ADDRESS,
      fetchPolicy: "network-only",
      context: createRequestContext({ sessionStrategy: "REDIRECT_TO_LOGIN" })
    });

    if (!result.data) throw new Error("No response received");

    return result.data.getMyAddresses;
  };

  if (options?.runInBackground) {
    return handler();
  }

  return withGlobalSpinner(handler)();
};

export const saveProfileAddress = withGlobalSpinner(
  async function saveProfileAddress(profileAddress: AddressInput) {
    const result = await getClient().mutate<{
      saveMyAddressProfile: AddressType[];
    }>({
      mutation: SAVE_ADDRESS_PROFILE,
      variables: {
        address: profileAddress
      },
      context: createRequestContext({ sessionStrategy: "REDIRECT_TO_LOGIN" })
    });

    if (!result.data) throw new Error("No response received");

    return result.data.saveMyAddressProfile;
  }
);

export const updateProfileAddress = withGlobalSpinner(
  async function updateProfileAddress(address: AddressInput) {
    const result = await getClient().mutate<{
      updateMyAddressProfile: AddressType[];
    }>({
      mutation: UPDATE_ADDRESS_PROFILE,
      variables: {
        address
      },
      context: createRequestContext({ sessionStrategy: "REDIRECT_TO_LOGIN" })
    });

    if (!result.data) throw new Error("No response received");

    return result.data.updateMyAddressProfile;
  }
);

export const deleteProfileAddress = withGlobalSpinner(
  async function deleteProfileAddress(addressKey: string) {
    const result = await getClient().mutate<{
      deleteMyAddressProfile: AddressType[];
    }>({
      mutation: DELETE_ADDRESS_PROFILE,
      variables: {
        addressKey
      },
      context: createRequestContext({ sessionStrategy: "REDIRECT_TO_LOGIN" })
    });

    if (!result.data) throw new Error("No response received");

    return result?.data?.deleteMyAddressProfile;
  }
);

export const listOrders = withGlobalSpinner(async function listOrders() {
  const result = await getClient().query<{ listOrders: BasicOrderType[] }>({
    query: LIST_ORDERS,
    fetchPolicy: "network-only",
    context: createRequestContext({ sessionStrategy: "REDIRECT_TO_LOGIN" })
  });

  if (!result.data) throw new Error("No response received");

  return result.data.listOrders;
});

export const getOrderById = withGlobalSpinner(async function getOrderById(
  id: string
) {
  const result = await getClient().query<{
    getOrder: OrderRecordType | null;
  }>({
    query: GET_ORDER,
    variables: { orderNumber: id },
    context: createRequestContext({
      usePostForQuery: true,
      sessionStrategy: "REDIRECT_TO_LOGIN"
    })
  });

  if (!result.data) throw new Error("No response received");

  return result.data.getOrder;
});

export const GET_MEMBERSHIPS = gql`
  query getMemberships($membershipIds: [ID!]) {
    getMemberships: getMemberships(membershipIds: $membershipIds) {
      ...MembershipFragment
    }
  }
  ${MEMBERSHIP_FRAGMENT}
`;

export const getMemberships = withGlobalSpinner(async function getMemberships(
  membershipIds: string[] = []
) {
  const result = await getClient().query<{
    getMemberships: MembershipType[];
  }>({
    query: GET_MEMBERSHIPS,
    variables: { membershipIds },
    context: createRequestContext({ sessionStrategy: "REDIRECT_TO_LOGIN" })
  });

  if (!result.data) throw new Error("No response received");

  return result.data.getMemberships;
});

export const GET_VARIANTS = gql`
  query getVariants($variantSkus: [ID!]) {
    getVariants(variantSkus: $variantSkus) {
      ...VariantFragment
    }
  }
  ${VARIANT_FRAGMENT}
`;

export const GET_VARIANTS_V2 = gql`
  query getVariantsV2($variantSkus: [ID!]!) {
    getVariantsV2(variantSkus: $variantSkus) {
      ...VariantFragmentV2
    }
  }
  ${VARIANT_FRAGMENT_V2}
`;

export const getVariants = async (
  variantSkus: string[]
): Promise<VariantType[]> => {
  const result = await getClient().query({
    query: GET_VARIANTS,
    variables: {
      variantSkus
    }
  });

  return result?.data?.getVariants;
};

export const getVariantsV2 = async (
  variantSkus: string[]
): Promise<VariantTypeV2[]> => {
  const result = await getClient().query({
    query: GET_VARIANTS_V2,
    variables: {
      variantSkus
    }
  });

  return result?.data?.getVariantsV2;
};

/* START: MyMembership Page */
const GET_MY_MEMBERSHIP = gql`
  query getMyMembership {
    getMyMembership {
      ...MemberInfoFragment
    }
  }
  ${MEMBER_INFO_FRAGMENT}
`;
export const getMyMembership = withGlobalSpinner(
  async function getMyMembership() {
    const result = await getClient().query<{
      getMyMembership: MemberInfoType;
    }>({
      query: GET_MY_MEMBERSHIP,
      fetchPolicy: "no-cache",
      context: createRequestContext({ sessionStrategy: "REDIRECT_TO_LOGIN" })
    });
    if (!result.data) throw new Error("No response received");

    return result.data.getMyMembership;
  }
);

const GET_MEMBER_STATUS = gql`
  query getMemberStatus {
    me {
      memberStatus {
        customerNumber
        metaData {
          offersAccepted {
            subscriptionId
            price
            dateAccepted
            offerType
          }
        }
      }
    }
  }
`;

export const getMemberStatus = withGlobalSpinner(
  async function getMemberStatus() {
    const result = await getClient().query<{
      me: UpsMeType;
    }>({
      query: GET_MEMBER_STATUS,
      fetchPolicy: "no-cache",
      context: createRequestContext({ sessionStrategy: "REDIRECT_TO_LOGIN" })
    });
    if (!result.data) throw new Error("No response received");

    return result.data.me.memberStatus;
  }
);

const CANCEL_MY_MEMBERSHIP = gql`
  mutation cancelMyMembership($reasonCode: String, $reasonFreeText: String) {
    cancelMyMembership(
      reasonCode: $reasonCode
      reasonFreeText: $reasonFreeText
    ) {
      ...MemberInfoFragment
    }
  }
  ${MEMBER_INFO_FRAGMENT}
`;
export const cancelMyMembership = withGlobalSpinner(
  async function cancelMyMembership(
    reasonCode?: string,
    reasonFreeText?: string
  ) {
    const result = await getClient().mutate<{
      cancelMyMembership: MemberInfoType;
    }>({
      mutation: CANCEL_MY_MEMBERSHIP,
      variables: { reasonCode, reasonFreeText },
      fetchPolicy: "no-cache",
      context: createRequestContext({ sessionStrategy: "REDIRECT_TO_LOGIN" })
    });
    if (!result.data) throw new Error("No response received");

    return result.data.cancelMyMembership;
  }
);

const REMOVE_SCHEDULED_CANCELLATION = gql`
  mutation removeScheduledCancellation {
    removeScheduledCancellation {
      ...MemberInfoFragment
    }
  }
  ${MEMBER_INFO_FRAGMENT}
`;

export const removeScheduledCancellation = withGlobalSpinner(
  async function removeScheduledCancellation() {
    const result = await getClient().mutate<{
      removeScheduledCancellation: MemberInfoType;
    }>({
      mutation: REMOVE_SCHEDULED_CANCELLATION,
      fetchPolicy: "no-cache",
      context: createRequestContext({ sessionStrategy: "REDIRECT_TO_LOGIN" })
    });
    if (!result.data) throw new Error("No response received");

    return result.data.removeScheduledCancellation;
  }
);

const MODIFY_MY_MEMBERSHIP = gql`
  mutation modifyMyMembership($toMembershipSku: String!) {
    modifyMyMembership(toMembershipSku: $toMembershipSku) {
      ...MemberInfoFragment
    }
  }
  ${MEMBER_INFO_FRAGMENT}
`;
export const modifyMyMembership = withGlobalSpinner(
  async function modifyMyMembership(toMembershipSku: string) {
    const result = await getClient().mutate<{
      modifyMyMembership: MemberInfoType;
    }>({
      mutation: MODIFY_MY_MEMBERSHIP,
      variables: { toMembershipSku },
      fetchPolicy: "no-cache",
      context: createRequestContext({ sessionStrategy: "REDIRECT_TO_LOGIN" })
    });
    if (!result.data) throw new Error("No response received");

    return result.data.modifyMyMembership;
  }
);

const MODIFY_MEMBERSHIP = gql`
  mutation modifyMembership(
    $subscriptionId: String
    $modifications: MembershipModificationsInput!
  ) {
    modifyMembership(
      modifications: $modifications
      subscriptionId: $subscriptionId
    ) {
      ...MemberInfoFragment
    }
  }
  ${MEMBER_INFO_FRAGMENT}
`;

export const modifyMembership = withGlobalSpinner(
  async function modifyMembership(
    subscriptionId: string, // moving forward, subscription id is not optional
    modifications: MembershipModificationsInput
  ) {
    const result = await getClient().mutate<{
      modifyMembership: MemberInfoType;
    }>({
      fetchPolicy: "no-cache",
      mutation: MODIFY_MEMBERSHIP,
      variables: {
        subscriptionId,
        modifications
      },
      context: createRequestContext({ sessionStrategy: "REDIRECT_TO_LOGIN" })
    });

    if (!result.data) throw new Error("No response received");

    return result.data.modifyMembership;
  }
);

const GET_MY_REORDER_PRODUCTS = gql`
  query getMyReorderProducts {
    getMyReorderProducts {
      sectionTitle
      carouselType
      showsInBasket
      products {
        ...ReorderProductsFragment
      }
      version
    }
  }
  ${REORDER_PRODUCTS_FRAGMENT}
`;

const getMyReorderProductsInner = async () => {
  const result = await getClient().query<{
    getMyReorderProducts: ProductSuggestionsType;
  }>({
    query: GET_MY_REORDER_PRODUCTS,
    context: createRequestContext({ sessionStrategy: "REDIRECT_TO_LOGIN" })
  });

  if (!result.data) throw new Error("No response received");

  return result.data.getMyReorderProducts;
};

export const getMyReorderProducts = async ({
  useGlobalSpinner = true
}: {
  useGlobalSpinner?: boolean;
} = {}): Promise<ProductSuggestionsType> => {
  if (!useGlobalSpinner) return getMyReorderProductsInner();

  return withGlobalSpinner(getMyReorderProductsInner)();
};

const REDEEM_MY_GIFT_MEMBERSHIP = gql`
  mutation redeemMyGiftMembership($redeemCode: String!) {
    redeemMyGiftMembership(redeemCode: $redeemCode) {
      ...MemberInfoFragment
    }
  }
  ${MEMBER_INFO_FRAGMENT}
`;
export const redeemMyGiftMembership = withGlobalSpinner(
  async function redeemMyGiftMembership(redeemCode: string) {
    const result = await getClient().mutate<{
      redeemMyGiftMembership: MemberInfoType;
    }>({
      mutation: REDEEM_MY_GIFT_MEMBERSHIP,
      variables: { redeemCode },
      fetchPolicy: "no-cache",
      context: createRequestContext({ sessionStrategy: "REDIRECT_TO_LOGIN" })
    });
    if (!result.data) throw new Error("No response received");

    return result.data.redeemMyGiftMembership;
  }
);
const GET_MY_PAYMENT_INFO = gql`
  query getMyPaymentInfo {
    getMyPaymentInfo {
      id
      type
      cardDetails {
        cardNumber
        cardType
        expiryMonth
        expiryYear
      }
      paypalDetails {
        accountNumber
      }
      billingAddress {
        firstName
        lastName
        lineOne
        lineTwo
        lineThree
        city
        postCode
        state
        countryCode
      }
    }
  }
`;
export const getMyPaymentInfo = withGlobalSpinner(
  async function getMyPaymentInfo() {
    const result = await getClient().query<{
      getMyPaymentInfo: MemberPaymentInfoType;
    }>({
      query: GET_MY_PAYMENT_INFO,
      fetchPolicy: "network-only",
      context: createRequestContext({ sessionStrategy: "REDIRECT_TO_LOGIN" })
    });

    if (!result.data) throw new Error("No response received");

    return result.data.getMyPaymentInfo;
  }
);

const GET_MY_NEXT_MEMBERSHIP_PAYMENT_SUMMARY = gql`
  query GetMyNextMembershipPaymentSummary {
    getMyNextMembershipPaymentSummary {
      subscriptionId
      paymentDate
      centAmount
      centAmountIncTax
      itemSku
      itemName
      itemPriceCentAmount
      dateFrom
      dateTo
      discountAppliedCentAmount
    }
  }
`;
export const getMyNextMembershipPaymentSummary = withGlobalSpinner(
  async function getMyNextMembershipPaymentSummary() {
    const result = await getClient().query<{
      getMyNextMembershipPaymentSummary: MembershipInvoiceSummaryType;
    }>({
      query: GET_MY_NEXT_MEMBERSHIP_PAYMENT_SUMMARY,
      fetchPolicy: "network-only",
      context: createRequestContext({ sessionStrategy: "REDIRECT_TO_LOGIN" })
    });

    if (!result.data) throw new Error("No response received");

    return result.data.getMyNextMembershipPaymentSummary;
  }
);
/* END: MyMembership Page */

const GET_ADVOCATE_CODE = gql`
  query getAdvocateCode {
    getAdvocateCode
  }
`;

const GET_RAF_CAMPAIGN_DATA = gql`
  query getRafCampaignData {
    getRafCampaignData {
      friendsJoined
      totalCreditEarned
      remainingCredit
    }
  }
`;

const REDEEM_REFERRAL_CODE = gql`
  mutation redeemReferralCode($referralCode: String!) {
    redeemReferralCode(referralCode: $referralCode) {
      ...BasketUpdateFragment
    }
  }
  ${BASKET_UPDATE_FRAGMENT}
`;

export const redeemReferralCode = withGlobalSpinner(
  async (referralCode: string) => {
    const { data } = await getClient().mutate({
      mutation: REDEEM_REFERRAL_CODE,
      variables: {
        referralCode
      },
      context: createRequestContext({ sessionStrategy: "START_NEW_ANONYMOUS" })
    });

    if (data?.redeemReferralCode) {
      const {
        items: basket,
        discounts,
        pricing,
        includedDiscounts,
        rejectedDiscounts,
        hasOnlyVirtualProducts,
        deliveryMethodName
      } = data.redeemReferralCode;

      setUser({
        basket,
        discounts,
        pricing,
        includedDiscounts,
        rejectedDiscounts,
        hasOnlyVirtualProducts: !!hasOnlyVirtualProducts,
        deliveryMethodName: removeNull(deliveryMethodName)
      });
    }

    return data;
  }
);

export const getAdvocateCode = async (): Promise<string> => {
  const { data } = await getClient().query<{
    getAdvocateCode: string;
  }>({
    query: GET_ADVOCATE_CODE,
    context: createRequestContext({ sessionStrategy: "REDIRECT_TO_LOGIN" })
  });
  return data.getAdvocateCode;
};

export const getRafCampaignData = withGlobalSpinner(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async function getCampaignData(): Promise<any> {
    const { data } = await getClient().query<{
      getRafCampaignData: string;
    }>({
      query: GET_RAF_CAMPAIGN_DATA,
      context: createRequestContext({ sessionStrategy: "REDIRECT_TO_LOGIN" })
    });
    return data.getRafCampaignData;
  }
);

const IDENTIFY_VISITOR = gql`
  mutation identifyVisitorV2(
    $platform: ClientPlatform!
    $sealedClientResults: String!
  ) {
    identifyVisitorV2(
      platform: $platform
      sealedClientResults: $sealedClientResults
    ) {
      bpFingerprintId
      userType
    }
  }
`;

export const identifyVisitor = async (
  platform: ClientPlatform,
  sealedClientResults: string
): Promise<BPFingerprintType> => {
  const result = await getClient().mutate({
    mutation: IDENTIFY_VISITOR,
    variables: {
      platform,
      sealedClientResults
    }
  });
  if (!result.data)
    return {
      bpFingerprintId: "",
      userType: "new"
    };
  return result.data.identifyVisitorV2;
};
